import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import moment from 'moment';
import toast from 'react-hot-toast';

import MilestoneFormDialog from './MilestoneFormDialog';
import DeleteMilestoneDialog from './DeleteMilestoneDialog';
import RevertToDraftDialog from './RevertToDraftDialog';
import SowAddendumFormDialog from './SowAddendumFormDialog';
import SowAddendumRequiredDialog from './SowAddendumRequiredDialog';
import httpClient from 'utils/httpClient';
import { getApiErrorPayload } from 'utils/apiError';
import { IND_RUPEE } from 'constant';

const STATUS_COLOR_MAP = {
  pending: 'info',
  in_progress: 'warning',
  under_review: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'error',
  superseded: 'default',
};

const STATUS_LABEL_MAP = {
  pending: 'Pending',
  in_progress: 'Inprogress',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  superseded: 'Superseded',
};

const formatDuration = (duration, unit) => {
  if (!duration) {
    return '-';
  }

  return [duration, unit].filter(Boolean).join(' ');
};

const formatCurrency = (value, currencySymbol = IND_RUPEE) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return '-';
  }

  return `${currencySymbol}${numericValue.toLocaleString()}`;
};

const getStatusColor = (milestone) => {
  const statusKey = milestone?.status?.toLowerCase();

  if (!statusKey) {
    return 'default';
  }

  // Statuses that should always use their mapped color, regardless of dates
  const statusOnlyColors = [
    'pending',
    'in_progress',
    'under_review',
    'approved',
    'rejected',
    'cancelled',
    'superseded',
  ];

  if (statusOnlyColors.includes(statusKey)) {
    return STATUS_COLOR_MAP[statusKey] || 'default';
  }

  // For other statuses (in_progress, active), apply date-based warnings
  const hasEndDate = Boolean(milestone?.end_date);
  if (hasEndDate) {
    const endDateMoment = moment(milestone.end_date);

    if (endDateMoment.isValid()) {
      const today = moment().startOf('day');
      const endDate = endDateMoment.startOf('day');

      if (endDate.isSameOrBefore(today)) {
        return 'error';
      }

      if (endDate.diff(today, 'days') <= 5) {
        return 'warning';
      }
    }
  }

  return STATUS_COLOR_MAP[statusKey] || 'default';
};

export const MilestonesTable = ({
  milestones,
  formatDate,
  onEdit,
  onDelete,
  onCancel,
  isSignedSow = false,
  cancellingId,
}) => (
  <TableContainer
    component={Paper}
    variant='outlined'
    sx={{ mt: 3, borderRadius: 1 }}
  >
    <Table size='small' aria-label='Milestones table'>
      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Est. Hours</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Freelancer Amount</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Client Amount</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {milestones.map((milestone) => {
          const isCancelled =
            milestone?.status?.toLowerCase?.() === 'cancelled';
          return (
            <TableRow key={milestone.id} hover>
              <TableCell>
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {milestone.title}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {formatDuration(milestone.duration, milestone.duration_unit)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {formatDate(milestone.start_date)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {formatDate(milestone.end_date)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {milestone.estimated_hours || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {formatCurrency(milestone.freelancer_payout)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {formatCurrency(milestone.billable_amount)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={STATUS_LABEL_MAP[milestone.status] || '--'}
                  color={getStatusColor(milestone)}
                  size='small'
                  variant='outlined'
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip
                    title={
                      isCancelled
                        ? 'Cancelled milestones cannot be edited'
                        : 'Edit milestone'
                    }
                  >
                    <span>
                      <IconButton
                        size='small'
                        color='primary'
                        disabled={isCancelled}
                        onClick={(event) => {
                          event.currentTarget.blur();
                          onEdit(milestone);
                        }}
                        aria-label={`Edit ${milestone.title}`}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isCancelled ? (
                    <Tooltip title='Cancelled milestone cannot be deleted'>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          disabled
                          aria-label={`Cancel ${milestone.title}`}
                        >
                          <CancelIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : isSignedSow ? (
                    <Tooltip title='Cancel milestone'>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={(event) => onCancel?.(milestone, event)}
                          disabled={cancellingId === milestone.id}
                          aria-label={`Cancel ${milestone.title}`}
                        >
                          {cancellingId === milestone.id ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: 'inherit' }}
                            />
                          ) : (
                            <CancelIcon fontSize='small' />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  {!isCancelled && !isSignedSow ? (
                    <Tooltip title='Delete milestone'>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={(event) => onDelete?.(milestone, event)}
                          aria-label={`Delete ${milestone.title}`}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export const MilestoneEmptyState = ({ sowId, onCreate }) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Typography variant='body1' color='text.secondary'>
      {sowId
        ? 'Create the first milestone to get started.'
        : 'Select a statement of work to view milestones.'}
    </Typography>
    {sowId && (
      <Button
        sx={{ mt: 2 }}
        startIcon={<AddIcon />}
        onClick={onCreate}
        aria-label='Create first milestone'
      >
        Create Milestone
      </Button>
    )}
  </Box>
);

export const MilestoneSummary = ({ totals }) => (
  <Box
    component='dl'
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
      gap: 2,
      m: 0,
      px: 3,
      pb: 3,
    }}
  >
    <Box component='div'>
      <Typography component='dt' variant='caption' color='text.secondary'>
        Total Freelancer Payout
      </Typography>
      <Typography component='dd' variant='body1' sx={{ fontWeight: 600, m: 0 }}>
        {formatCurrency(totals.freelancer)}
      </Typography>
    </Box>
    <Box component='div'>
      <Typography component='dt' variant='caption' color='text.secondary'>
        Total Billable Amount (Gross)
      </Typography>
      <Typography component='dd' variant='body1' sx={{ fontWeight: 600, m: 0 }}>
        {formatCurrency(totals.client)}
      </Typography>
    </Box>
  </Box>
);

const computeMilestoneSummary = (items) => {
  const aggregated = items.reduce(
    (accumulator, milestone) => {
      // Use freelancer_payout and billable_amount from the API response
      const freelancerAmount = Number(milestone.freelancer_payout || 0);
      const clientAmount = Number(milestone.billable_amount || 0);

      accumulator.totals.freelancer += Number.isFinite(freelancerAmount)
        ? freelancerAmount
        : 0;
      accumulator.totals.client += Number.isFinite(clientAmount)
        ? clientAmount
        : 0;

      if (milestone?.status?.toLowerCase() === 'completed') {
        accumulator.completed += 1;
      }

      return accumulator;
    },
    { totals: { freelancer: 0, client: 0 }, completed: 0 }
  );

  return {
    totals: aggregated.totals,
    completed: aggregated.completed,
    total: items.length,
  };
};

const MilestoneSection = ({
  sowId,
  projectId,
  sow,
  onMilestonesChange,
  onSowUpdate,
  onDocumentsRefresh,
  onDocumentsReset,
}) => {
  const [milestones, setMilestones] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [pendingMilestoneAction, setPendingMilestoneAction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [addendumRequiredOpen, setAddendumRequiredOpen] = useState(false);
  const [addendumFormOpen, setAddendumFormOpen] = useState(false);
  const isMountedRef = useRef(false);

  const formatDate = useCallback((date) => {
    if (!date) {
      return '-';
    }

    return moment(date).format('MMM DD, YYYY');
  }, []);

  const fetchMilestones = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    if (!sowId) {
      if (isMountedRef.current) {
        setMilestones([]);
        setIsFetching(false);
      }
      return;
    }

    setIsFetching(true);

    try {
      const response = await httpClient.get(
        `/api/v1/project-management/milestones/?project_id=${projectId}`
      );

      const { status, message, data } = response ?? {};

      console.log('data', data);

      if (status === false) {
        throw new Error(message || 'Failed to fetch milestones');
      }

      const results = data?.items ?? [];
      if (isMountedRef.current) {
        const nextMilestones = Array.isArray(results) ? results : [];
        setMilestones(nextMilestones);

        if (onMilestonesChange) {
          const summary = computeMilestoneSummary(nextMilestones);
          onMilestonesChange({
            milestoneCount: summary.total,
            totalFreelancerAmount: summary.totals.freelancer,
            totalClientAmount: summary.totals.client,
            completedCount: summary.completed,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching milestones:', error);
      toast.error(error?.message || 'Failed to fetch milestones');
      if (isMountedRef.current) {
        setMilestones([]);
        if (onMilestonesChange) {
          onMilestonesChange({
            milestoneCount: 0,
            totalFreelancerAmount: 0,
            totalClientAmount: 0,
            completedCount: 0,
          });
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [sowId, projectId, onMilestonesChange]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchMilestones();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchMilestones]);

  const milestoneSummary = useMemo(
    () => computeMilestoneSummary(milestones),
    [milestones]
  );
  const totals = milestoneSummary.totals || { freelancer: 0, client: 0 };

  const applyMilestoneDeletion = useCallback(
    (deletedId) => {
      setMilestones((prevMilestones) => {
        const nextMilestones = prevMilestones.filter(
          (milestone) => milestone.id !== deletedId
        );

        if (onMilestonesChange) {
          const summary = computeMilestoneSummary(nextMilestones);
          onMilestonesChange({
            milestoneCount: summary.total,
            totalFreelancerAmount: summary.totals.freelancer,
            totalClientAmount: summary.totals.client,
            completedCount: summary.completed,
          });
        }

        return nextMilestones;
      });
    },
    [onMilestonesChange]
  );

  const openCreateDialog = () => {
    setSelectedMilestone(null);
    setActiveDialog('create');
  };

  const openEditDialog = (milestone) => {
    setSelectedMilestone(milestone);
    setActiveDialog('edit');
  };

  const closeFormDialog = () => {
    setActiveDialog(null);
    setSelectedMilestone(null);
  };

  const handleFormSuccess = async () => {
    closeFormDialog();
    await fetchMilestones();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedMilestone(null);
  };

  const handleDeleteRequest = (milestone, event) => {
    event?.currentTarget?.blur();
    setSelectedMilestone(milestone);
    setDeleteDialogOpen(true);
  };

  const handleCancelRequest = (milestone, event) => {
    event?.currentTarget?.blur();
    if (cancellingId === milestone?.id) {
      return;
    }
    performCancelMilestone(milestone);
  };

  const performDeleteMilestone = async (
    milestone,
    shouldCloseDialog = true
  ) => {
    if (!milestone) return;

    try {
      setDeleteLoading(true);
      const response = await httpClient.delete(
        `/api/v1/project-management/milestones?ids=${milestone.id}`
      );

      if (response?.status === false) {
        throw new Error(response?.message || 'Failed to delete milestone');
      }

      toast.success(response?.message || 'Milestone deleted successfully');
      applyMilestoneDeletion(milestone.id);
      if (shouldCloseDialog) {
        closeDeleteDialog();
      }
    } catch (error) {
      console.error('Error deleting milestone:', error);

      const { code: errorCode, message: errorMessage } =
        getApiErrorPayload(error);

      if (errorCode === 'SOW_ACTIVE_OR_SIGNED_USE_CANCEL') {
        toast.error(
          errorMessage ||
            'Cannot delete milestones when SOW is ACTIVE or SIGNED. Use Cancel milestone to preserve audit trail.'
        );
        if (shouldCloseDialog) {
          closeDeleteDialog();
        }
        return;
      }

      if (errorCode === 'SOW_ACTIVE_REVERT_REQUIRED') {
        setPendingMilestoneAction({ milestone, type: 'delete' });
        setRevertDialogOpen(true);
        if (shouldCloseDialog) {
          closeDeleteDialog();
        }
        return;
      }

      if (errorCode === 'SOW_SIGNED_ADDENDUM_REQUIRED') {
        toast.error(errorMessage || 'Failed to delete milestone');
        if (shouldCloseDialog) {
          closeDeleteDialog();
        }
        return;
      }

      toast.error(errorMessage || 'Failed to delete milestone');
      if (shouldCloseDialog) {
        closeDeleteDialog();
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const performCancelMilestone = async (milestone) => {
    if (!milestone) return;

    try {
      setDeleteLoading(true);
      setCancellingId(milestone.id);
      const response = await httpClient.post(
        `/api/v1/project-management/milestones/cancel?ids=${milestone.id}`
      );

      if (response?.status === false) {
        throw new Error(response?.message || 'Failed to cancel milestone');
      }

      toast.success(response?.message || 'Milestone cancelled successfully');
      await fetchMilestones();
    } catch (error) {
      console.error('Error cancelling milestone:', error);

      const { code: errorCode, message: errorMessage } =
        getApiErrorPayload(error);

      if (errorCode === 'SOW_ACTIVE_REVERT_REQUIRED') {
        setPendingMilestoneAction({ milestone, type: 'cancel' });
        setRevertDialogOpen(true);
        return;
      }

      if (errorCode === 'SOW_SIGNED_ADDENDUM_REQUIRED') {
        toast.error(errorMessage || 'Failed to cancel milestone');
        return;
      }

      toast.error(errorMessage || 'Failed to cancel milestone');
    } finally {
      setDeleteLoading(false);
      setCancellingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMilestone) return;
    await performDeleteMilestone(selectedMilestone);
  };

  const handleRevertToDraft = async () => {
    if (!sowId) {
      toast.error('SOW ID is missing');
      return;
    }

    setRevertLoading(true);

    try {
      const response = await httpClient.post(
        `/api/v1/project-management/sows/${sowId}/revert-to-draft/`
      );

      if (response?.status === false) {
        throw new Error(
          response?.message || 'Failed to revert SOW to draft status'
        );
      }

      toast.success(response?.message || 'SOW reverted to DRAFT successfully');

      // Update sow state: set documents to null and update status
      if (onSowUpdate) {
        onSowUpdate({
          status: 'draft',
          client_document: null,
          freelancer_document: null,
          document_context: {
            action: 'generate_sow',
            has_signed_sow: false,
            has_draft_sow: true,
            has_draft_addendum: false,
            draft_sow_id: sowId || null,
            draft_addendum_id: null,
          },
          ...(response?.data || {}),
        });
      }

      // Perform the delete operation with pending milestone
      if (pendingMilestoneAction?.milestone) {
        const { milestone, type } = pendingMilestoneAction;
        if (type === 'cancel') {
          await performCancelMilestone(milestone);
        } else {
          await performDeleteMilestone(milestone, false);
        }
        setPendingMilestoneAction(null);
      }

      onDocumentsRefresh?.();

      // Close the revert dialog after all follow-up work is done
      setRevertDialogOpen(false);
    } catch (error) {
      console.error('Error reverting SOW to draft:', error);
      toast.error(error?.message || 'Failed to revert SOW to draft status');
    } finally {
      setRevertLoading(false);
    }
  };

  const hasMilestones = milestones.length > 0;

  return (
    <>
      <Paper sx={{ mb: 4, borderRadius: 2 }}>
        {isFetching && (
          <LinearProgress
            aria-label='Loading milestones'
            sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
          />
        )}

        <Box sx={{ p: 3, pb: hasMilestones ? 3 : 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Milestones ({milestones.length})
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Track progress, budgets, and delivery timelines.
              </Typography>
            </Box>

            {hasMilestones && (
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                disabled={!sowId}
                aria-label='Add milestone'
              >
                Add Milestone
              </Button>
            )}
          </Box>

          {hasMilestones ? (
            <MilestonesTable
              milestones={milestones}
              formatDate={formatDate}
              onEdit={openEditDialog}
              onDelete={handleDeleteRequest}
              onCancel={handleCancelRequest}
              isSignedSow={sow?.status?.toLowerCase() === 'signed'}
              cancellingId={cancellingId}
            />
          ) : (
            <MilestoneEmptyState sowId={sowId} onCreate={openCreateDialog} />
          )}
        </Box>

        {hasMilestones && <MilestoneSummary totals={totals} />}
      </Paper>

      <MilestoneFormDialog
        open={activeDialog === 'create' || activeDialog === 'edit'}
        sowId={sowId}
        sow={sow}
        editingMilestone={activeDialog === 'edit' ? selectedMilestone : null}
        existingMilestones={milestones}
        onClose={closeFormDialog}
        onSuccess={handleFormSuccess}
        onSowUpdate={onSowUpdate}
        onDocumentsReset={onDocumentsReset}
      />

      <DeleteMilestoneDialog
        open={deleteDialogOpen}
        mode='delete'
        milestone={deleteDialogOpen ? selectedMilestone : null}
        onCancel={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <RevertToDraftDialog
        open={revertDialogOpen}
        title='Delete Milestone (Regenerate SOW)'
        description='Milestones can only be deleted before the SOW is generated. To delete them, you need to regenerate the SOW.'
        note='This action will delete all existing documents.'
        loading={revertLoading}
        onCancel={() => {
          setRevertDialogOpen(false);
          setPendingMilestoneAction(null);
        }}
        onProceed={handleRevertToDraft}
      />
      <SowAddendumRequiredDialog
        open={addendumRequiredOpen}
        onCancel={() => setAddendumRequiredOpen(false)}
        onProceed={() => {
          setAddendumRequiredOpen(false);
          setAddendumFormOpen(true);
        }}
      />
      <SowAddendumFormDialog
        open={addendumFormOpen}
        onClose={() => setAddendumFormOpen(false)}
      />
    </>
  );
};

export default MilestoneSection;
